import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomerDetails, PostalAddress } from 'apiClient/Api';

const addrHasContent = (a?: PostalAddress | null): boolean =>
    !!(a && (a.name || a.street || a.zipCode || a.city || a.country || a.addition));

/** Editierbare Adressfelder (Rechnungs-/Lieferadresse). */
const AddressFields: React.FC<{ value?: PostalAddress | null; onChange: (a: PostalAddress) => void }> = ({
    value,
    onChange,
}) => {
    const { t } = useTranslation();
    const a = value || {};
    const set = (p: Partial<PostalAddress>) => onChange({ ...a, ...p });
    return (
        <Grid container spacing={1.5}>
            <Grid size={12}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('calculation.details.company', 'Firma / Empfänger')}
                    value={a.name || ''}
                    onChange={(e) => set({ name: e.target.value })}
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('calculation.details.street', 'Straße und Hausnummer')}
                    value={a.street || ''}
                    onChange={(e) => set({ street: e.target.value })}
                />
            </Grid>
            <Grid size={{ xs: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('calculation.details.zip', 'PLZ')}
                    value={a.zipCode || ''}
                    onChange={(e) => set({ zipCode: e.target.value })}
                />
            </Grid>
            <Grid size={{ xs: 8 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('calculation.details.city', 'Ort')}
                    value={a.city || ''}
                    onChange={(e) => set({ city: e.target.value })}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('calculation.details.country', 'Land')}
                    value={a.country || ''}
                    onChange={(e) => set({ country: e.target.value })}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('calculation.details.addition', 'Adresszusatz')}
                    value={a.addition || ''}
                    onChange={(e) => set({ addition: e.target.value })}
                />
            </Grid>
        </Grid>
    );
};

/** Editierbares Formular für die Kunden-Stammdaten (Kontakt + Adressen + Nummern). */
export const CustomerDetailsForm: React.FC<{
    value: CustomerDetails;
    onChange: (d: CustomerDetails) => void;
}> = ({ value, onChange }) => {
    const { t } = useTranslation();
    const set = (p: Partial<CustomerDetails>) => onChange({ ...value, ...p });
    const [sameDelivery, setSameDelivery] = useState(!addrHasContent(value.deliveryAddress));

    const setBilling = (a: PostalAddress) =>
        onChange({ ...value, billingAddress: a, ...(sameDelivery ? { deliveryAddress: a } : {}) });

    const toggleSameDelivery = (checked: boolean) => {
        setSameDelivery(checked);
        onChange({ ...value, deliveryAddress: checked ? value.billingAddress || {} : value.deliveryAddress || {} });
    };

    return (
        <Stack spacing={2}>
            <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('calculation.details.contact', 'Ansprechpartner')}
                        value={value.contactPerson || ''}
                        onChange={(e) => set({ contactPerson: e.target.value })}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('calculation.details.phone', 'Telefon')}
                        value={value.phone || ''}
                        onChange={(e) => set({ phone: e.target.value })}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('calculation.details.internalNumber', 'Interne Nummer')}
                        value={value.internalNumber || ''}
                        onChange={(e) => set({ internalNumber: e.target.value })}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('calculation.details.procurementNumber', 'Beschaffungsnummer')}
                        value={value.procurementNumber || ''}
                        onChange={(e) => set({ procurementNumber: e.target.value })}
                    />
                </Grid>
            </Grid>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('calculation.details.billing', 'Rechnungsadresse')}
                </Typography>
                <AddressFields value={value.billingAddress} onChange={setBilling} />
            </Box>

            <Box>
                <FormControlLabel
                    control={<Checkbox checked={sameDelivery} onChange={(e) => toggleSameDelivery(e.target.checked)} />}
                    label={t('calculation.details.sameDelivery', 'Lieferadresse entspricht der Rechnungsadresse')}
                />
                {!sameDelivery && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('calculation.details.delivery', 'Lieferadresse')}
                        </Typography>
                        <AddressFields value={value.deliveryAddress} onChange={(a) => set({ deliveryAddress: a })} />
                    </Box>
                )}
            </Box>
        </Stack>
    );
};

const addressLines = (a?: PostalAddress | null): string[] => {
    if (!a) return [];
    const lines = [
        a.name,
        a.addition,
        a.street,
        [a.zipCode, a.city].filter(Boolean).join(' '),
        a.country,
    ].filter((l) => l && l.trim());
    return lines as string[];
};

export const hasCustomerDetails = (d?: CustomerDetails | null): boolean => {
    if (!d) return false;
    return !!(
        d.contactPerson ||
        d.phone ||
        d.internalNumber ||
        d.procurementNumber ||
        addressLines(d.billingAddress).length ||
        addressLines(d.deliveryAddress).length
    );
};

/** Read-only Darstellung der Kunden-Stammdaten (für Office / „Meine Kalkulationen"). */
export const CustomerDetailsView: React.FC<{ details?: CustomerDetails | null }> = ({ details }) => {
    const { t } = useTranslation();
    if (!hasCustomerDetails(details)) return null;
    const d = details!;
    const billing = addressLines(d.billingAddress);
    const delivery = addressLines(d.deliveryAddress);
    return (
        <Stack spacing={1.5}>
            {(d.contactPerson || d.phone || d.internalNumber || d.procurementNumber) && (
                <Grid container spacing={1}>
                    {d.contactPerson && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('calculation.details.contact', 'Ansprechpartner')}
                            </Typography>
                            <Typography variant="body2">{d.contactPerson}</Typography>
                        </Grid>
                    )}
                    {d.phone && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('calculation.details.phone', 'Telefon')}
                            </Typography>
                            <Typography variant="body2">{d.phone}</Typography>
                        </Grid>
                    )}
                    {d.internalNumber && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('calculation.details.internalNumber', 'Interne Nummer')}
                            </Typography>
                            <Typography variant="body2">{d.internalNumber}</Typography>
                        </Grid>
                    )}
                    {d.procurementNumber && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('calculation.details.procurementNumber', 'Beschaffungsnummer')}
                            </Typography>
                            <Typography variant="body2">{d.procurementNumber}</Typography>
                        </Grid>
                    )}
                </Grid>
            )}
            <Grid container spacing={2}>
                {billing.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('calculation.details.billing', 'Rechnungsadresse')}
                        </Typography>
                        {billing.map((l, i) => (
                            <Typography key={i} variant="body2">
                                {l}
                            </Typography>
                        ))}
                    </Grid>
                )}
                {delivery.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('calculation.details.delivery', 'Lieferadresse')}
                        </Typography>
                        {delivery.map((l, i) => (
                            <Typography key={i} variant="body2">
                                {l}
                            </Typography>
                        ))}
                    </Grid>
                )}
            </Grid>
        </Stack>
    );
};
